<template>
  <nav class="sidebar-navbar">
    <!-- Header -->
    <div
      class="navbar-header"
      :class="{
        'is-translucent': !anyOtherModalOpen,
        'is-open': menuOpen
      }"
    >
      <button
        class="menu-toggle"
        :class="{ 'is-active': menuOpen }"
        :aria-label="$t('menu.toggle')"
        @click="toggleMenu"
      >
        <span></span>
        <span></span>
        <span></span>
      </button>

      <router-link
        to="/home"
        class="logo-link"
      >
        <img
          src="../assets/images/logo_dark.png"
          alt="Energy2Go"
          height="34"
          class="logo-img"
        >
      </router-link>

      <div class="navbar-actions">
        <img
          v-if="ENABLE_CHATBOT"
          class="chatbot-btn clickable"
          :class="{ active: showChatBot && !anyOtherModalOpen }"
          src="@/assets/images/chat_ai_2.png"
          alt="Chatbot"
          @click="openChatBot"
        >
        <a
          v-else
          :href="$t('login.whatsapp_msg')"
          target="_blank"
          class="whatsapp-link"
        >
          <font-awesome-icon :icon="{ prefix: 'fab', iconName: 'whatsapp' }" />
        </a>
      </div>
    </div>

    <!-- Menu Dropdown -->
    <div
      v-if="menuOpen"
      class="menu-dropdown"
      :class="{ 'is-translucent': !anyOtherModalOpen }"
    >
      <!-- User Info -->
      <div class="user-section">
        <div class="user-avatar">
          <span class="user-initial">{{ userInitial }}</span>
        </div>
        <div class="user-info">
          <div class="user-name">
            {{ name || (email ? email.split('@')[0] : 'Usuário') }}
          </div>
          <div class="user-email">
            {{ email }}
          </div>
        </div>
      </div>

      <!-- Menu Items -->
      <nav class="menu-items">
        <router-link
          to="/home"
          class="menu-item"
          active-class="active"
          @click="closeMenu"
        >
          <font-awesome-icon icon="house" />
          <span>{{ $t('home.take.home') }}</span>
        </router-link>

        <router-link
          :to="disablePaymentMethods ? '#' : '/payment-methods'"
          class="menu-item"
          :class="{
            active: $route.path === '/payment-methods',
            disabled: disablePaymentMethods
          }"
          @click="!disablePaymentMethods && closeMenu()"
        >
          <font-awesome-icon icon="credit-card" />
          <span>{{ $t('home.take.payment_methods') }}</span>
        </router-link>

        <router-link
          to="/history"
          class="menu-item"
          active-class="active"
          @click="closeMenu"
        >
          <font-awesome-icon icon="clock-rotate-left" />
          <span>{{ $t('home.take.history') }}</span>
        </router-link>

        <router-link
          to="/maps"
          class="menu-item"
          active-class="active"
          @click="closeMenu"
        >
          <font-awesome-icon icon="map-location-dot" />
          <span>{{ $t('home.take.maps_menu') }}</span>
        </router-link>

        <a
          href="#"
          class="menu-item"
          @click.prevent="priceTableClick"
        >
          <font-awesome-icon icon="dollar-sign" />
          <span>{{ $t('home.take.price_table') }}</span>
        </a>

        <a
          href="https://www.energy2go.com.br/eventos"
          target="_blank"
          rel="noopener noreferrer"
          class="menu-item"
        >
          <font-awesome-icon icon="circle-question" />
          <span>{{ $t('home.take.need_help') }}</span>
        </a>

        <router-link
          to="/logout"
          class="menu-item logout"
          @click="closeMenu"
        >
          <font-awesome-icon icon="power-off" />
          <span>{{ $t('home.take.logout') }}</span>
        </router-link>
      </nav>

      <!-- Preferences Section -->
      <div class="preferences-section">
        <div class="language-switch">
          <span
            v-if="!external && language === 'en'"
            class="clickable"
            @click="set_lang('pt-BR')"
          >
            <font-awesome-icon
              icon="globe"
              class="me-1"
            />
            Mudar para <strong>Português</strong>
          </span>
          <span
            v-if="!external && language === 'pt-BR'"
            class="clickable"
            @click="set_lang('en')"
          >
            <font-awesome-icon
              icon="globe"
              class="me-1"
            />
            Switch to <strong>English</strong>
          </span>
        </div>
      </div>

      <!-- Version -->
      <div class="menu-footer">
        <AppVersion />
      </div>
    </div>

    <!-- Overlay backdrop -->
    <div
      v-if="menuOpen"
      class="menu-backdrop"
      @click="closeMenu"
    ></div>
  </nav>
</template>

<script>
  import AppVersion from '@/components/AppVersion.vue'
  import { PRICE_TABLE } from '@/constants.js'

  const ENABLE_CHATBOT = import.meta.env.VITE_ENABLE_CHATBOT === 'true'

  export default {
    name: 'SideBar',
    components: {
      AppVersion
    },
    props: {
      name: {
        type: String,
        default: null
      },
      email: {
        type: String,
        default: null
      },
      showChatBot: {
        type: Boolean,
        default: false
      },
      anyOtherModalOpen: {
        type: Boolean,
        default: false
      },
      disablePaymentMethods: {
        type: Boolean,
        default: false
      }
    },
    emits: [PRICE_TABLE, 'openChat'],
    data() {
      return {
        PRICE_TABLE,
        ENABLE_CHATBOT,
        language: 'pt-BR',
        external: false,
        menuOpen: false
      }
    },
    computed: {
      userInitial() {
        if (this.name) {
          return this.name.trim().charAt(0).toUpperCase()
        }
        if (this.email) {
          return this.email.trim().charAt(0).toUpperCase()
        }
        return 'U'
      }
    },
    watch: {
      anyOtherModalOpen(val) {
        if (val) this.closeMenu()
      }
    },
    mounted() {
      let lang = sessionStorage.getItem('lang')
      this.set_lang(lang)
    },
    methods: {
      set_lang: function (lang) {
        if (lang === undefined || lang === 'pt-BR') {
          sessionStorage.setItem('lang', 'pt-BR')
          this.$i18n.locale = 'pt'
          this.language = 'pt-BR'
        } else if (lang === 'en') {
          sessionStorage.setItem('lang', 'en')
          this.$i18n.locale = 'en'
          this.language = 'en'
        }
      },
      toggleMenu() {
        this.menuOpen = !this.menuOpen
      },
      closeMenu() {
        this.menuOpen = false
      },
      priceTable() {
        if (this.$route.name === 'home_page') {
          this.$emit('priceTable')
        } else {
          this.$router
            .push({
              name: 'home_page',
              query: { showPriceTable: Date.now().toString() }
            })
            .catch(() => {})
        }
        this.closeMenu()
      },
      priceTableClick() {
        this.priceTable()
      },
      openChatBot() {
        this.$emit('openChat')
      }
    }
  }
</script>

<style scoped lang="scss">
  @use '@/main';

  .sidebar-navbar {
    position: sticky;
    top: 0;
    z-index: 1600;
    width: 100%;
  }

  /* Menu Dropdown */
  .menu-dropdown {
    position: absolute;
    top: 100%;
    left: 1.5rem;
    width: calc(100% - 3rem);
    max-width: 320px;
    z-index: 1702;
    background: rgba(255, 255, 255, 0.95);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1) !important;
    overflow-y: auto;
    overflow-x: hidden;
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - var(--app-fixed-top-offset, 0px) - var(--app-fixed-bottom-offset, 0px) - 20px);
    max-height: calc(100dvh - var(--app-fixed-top-offset, 0px) - var(--app-fixed-bottom-offset, 0px) - 20px);
    border-radius: 0 0 16px 16px;

    &.is-translucent {
      background: rgba(255, 255, 255, 0.8);
      -webkit-backdrop-filter: blur(40px) saturate(180%);
      backdrop-filter: blur(40px) saturate(180%);
    }

    @media (max-width: 768px) {
      left: 0;
      width: 100%;
      max-width: none;
    }
  }

  /* Header */
  .navbar-header {
    position: relative;
    z-index: 1701;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 60px;
    padding: 0 1.5rem;
    background: rgba(255, 255, 255, 0.9);
    margin: 0 !important;
    border: 1px solid rgba(255, 255, 255, 0.8) !important;
    box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.15) !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    overflow: visible !important;
    border-radius: 16px;

    &.is-translucent {
      background: rgba(255, 255, 255, 0.6) !important;
      -webkit-backdrop-filter: blur(40px) saturate(180%) !important;
      backdrop-filter: blur(40px) saturate(180%) !important;
      border: 1px solid rgba(255, 255, 255, 0.4) !important;
    }

    &.is-open {
      background: rgba(255, 255, 255, 0.95) !important;
      border-radius: 16px 16px 0 0;
    }
  }

  .menu-toggle {
    background: none;
    border: none;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 5px;
    padding: 8px;
    width: 40px;
    height: 40px;
    position: relative;
    z-index: 10;

    span {
      width: 24px;
      height: 2px;
      background: #424242;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      border-radius: 2px;
      transform-origin: center;
    }

    &.is-active {
      span:nth-child(1) {
        transform: translateY(7px) rotate(45deg);
      }
      span:nth-child(2) {
        opacity: 0;
        transform: translateX(-10px);
      }
      span:nth-child(3) {
        transform: translateY(-7px) rotate(-45deg);
      }
    }
  }

  .logo-link {
    flex: 1;
    display: flex;
    justify-content: center;
    text-decoration: none;
    transition: all 0.3s ease;
  }

  .logo-img {
    height: 34px;
    cursor: pointer;
    transition: transform 0.2s;

    &:hover {
      transform: scale(1.05);
    }
  }

  .navbar-actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    width: auto;
    overflow: visible;
    padding: 0 8px;
  }

  .chatbot-btn {
    width: 37px;
    height: 32px;
    cursor: pointer;
    transition: all 0.3s ease;
    z-index: 101;
    border-radius: 50%;
    flex-shrink: 0;
    object-fit: contain;
    overflow: hidden;
    display: block;

    &:hover {
      transform: scale(1.1);
    }

    &.active {
      background: white;
      box-shadow: 0 0 12px rgba(255, 255, 255, 0.9);
      transform: scale(1.1);
    }
  }

  .whatsapp-link {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    background: #25d366;
    border-radius: 50%;
    color: white;
    text-decoration: none;
    transition: background-color 0.2s;

    &:hover {
      background-color: #1ebb59;
    }
  }

  /* Menu Dropdown */

  .user-section {
    padding: 1.25rem 1.5rem;
    margin-bottom: 0.25rem;
    border-bottom: 1px solid rgba(0, 0, 0, 0.04);
    background: rgba(0, 0, 0, 0.01);
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .user-avatar {
    flex-shrink: 0;
    width: 48px;
    height: 48px;
    border-radius: 16px;
    background: linear-gradient(135deg, #007aff 0%, #0056cc 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(0, 122, 255, 0.3);
  }

  .user-initial {
    color: white;
    font-weight: var(--fw-semibold);
    font-size: var(--fs-xl);
  }

  .user-info {
    flex: 1;
    min-width: 0;
  }

  .user-name {
    font-weight: var(--fw-semibold);
    font-size: var(--fs-lg);
    color: #1d1d1f;
    margin-bottom: 2px;
    letter-spacing: var(--ls-tight);
  }

  .user-email {
    font-size: var(--fs-sm);
    color: #86868b;
    font-weight: var(--fw-regular);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .menu-items {
    padding: 0.5rem 0;
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .menu-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0.65rem 1.5rem;
    color: #424245;
    text-decoration: none;
    border-radius: 10px;
    font-size: var(--fs-sm);
    font-weight: var(--fw-medium);
    transition: all 0.2s ease;

    &:hover {
      background: rgba(0, 0, 0, 0.03);
      color: #007aff;
    }

    &.active {
      background: rgba(0, 122, 255, 0.08);
      color: #007aff;
      font-weight: var(--fw-semibold);
    }

    &.disabled {
      opacity: 0.5;
      cursor: not-allowed;
      pointer-events: none;
      filter: grayscale(0.5);
    }

    &.logout {
      margin-top: 4px;
      color: #ff3b30;

      &:hover {
        background: rgba(255, 59, 48, 0.08);
        color: #ff3b30;
      }
    }

    svg {
      width: 18px;
      height: 18px;
      opacity: 0.7;
      transition: opacity 0.2s;
    }

    &:hover svg,
    &.active svg {
      opacity: 1;
    }
  }

  .preferences-section {
    padding: 0.75rem 1.5rem;
    border-top: 1px solid rgba(0, 0, 0, 0.04);
  }

  .language-switch {
    font-size: var(--fs-sm);

    span {
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 8px;
      color: #86868b;
      transition: color 0.2s;

      &:hover {
        color: #007aff;
      }

      strong {
        font-weight: var(--fw-semibold);
        color: #1d1d1f;
      }
    }
  }

  .menu-footer {
    padding: 0.75rem 1.5rem;
    text-align: center;
    font-size: var(--fs-xs);
    color: #86868b;
    border-top: 1px solid rgba(0, 0, 0, 0.04);
  }

  /* Backdrop */
  .menu-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.2);
    -webkit-backdrop-filter: blur(4px);
    backdrop-filter: blur(4px);
    z-index: 1699;
  }
</style>
